"use strict";
/*
 * Membrane React package
 * {@link https://www.npmjs.com/package/@membranehq/react}
 * v0.29.2
 */
'use client';

import { jsx } from 'react/jsx-runtime';
import { createContext, forwardRef, useContext } from 'react';

const splitVariantProps = (recipe, props) => {
  const variantProps = {};
  const otherProps = {};
  const keys = recipe.variantKeys.map(String);
  for (const key of Object.keys(props)) {
    if (keys.includes(key)) variantProps[key] = props[key];
    else otherProps[key] = props[key];
  }
  return [variantProps, otherProps];
};
const getDisplayName = (Component) => {
  if (typeof Component === "string") return Component;
  const named = Component;
  return named.displayName ?? named.name ?? "StyledSlot";
};
const createSlotRecipeContext = (recipe) => {
  const StyleContext = createContext(null);
  const withRootProvider = (Component) => {
    const StyledRootProvider = (props) => {
      const [variantProps, otherProps] = splitVariantProps(recipe, props);
      const slotStyles = recipe(variantProps);
      return /* @__PURE__ */ jsx(StyleContext.Provider, { value: slotStyles, children: /* @__PURE__ */ jsx(Component, { ...otherProps }) });
    };
    StyledRootProvider.displayName = getDisplayName(Component);
    return StyledRootProvider;
  };
  const withProvider = (Component, slot) => {
    const StyledSlotProvider = forwardRef((props, ref) => {
      const [variantProps, otherProps] = splitVariantProps(recipe, props);
      const slotStyles = recipe(variantProps);
      const slotFn = slotStyles[slot];
      return /* @__PURE__ */ jsx(StyleContext.Provider, { value: slotStyles, children: /* @__PURE__ */ jsx(Component, { ref, ...otherProps, className: slotFn({ className: props.className }) }) });
    });
    StyledSlotProvider.displayName = getDisplayName(Component);
    return StyledSlotProvider;
  };
  const withContext = (Component, slot) => {
    const StyledSlot = forwardRef((props, ref) => {
      const slotStyles = useContext(StyleContext);
      const slotFn = slotStyles?.[slot];
      const className = slotFn ? slotFn({ className: props.className }) : props.className;
      return /* @__PURE__ */ jsx(Component, { ref, ...props, className });
    });
    StyledSlot.displayName = getDisplayName(Component);
    return StyledSlot;
  };
  return {
    withRootProvider,
    withProvider,
    withContext
  };
};

export { createSlotRecipeContext };
